import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminDistributionPlatformsListPermissionsEnum,
  AdminGroup,
  AdminGroupPermission,
  AdminGroupPermissionsCreateRequest,
  AdminIssuerDetailPermissionsEnum,
  AdminIssuersDropdownListPermissionsEnum,
  AdminOrganization,
  AdminOrganizationDetails,
  AdminOrganizationsListPermissionsEnum,
  DropdownElement,
  AdminProductsDropdownListPermissionOnOrganizationEnum,
  DistributionPlatform,
  DistributionPlatformPermissionEnum,
  EnvironmentPermissionsEnum,
  OrganizationPermissionsEnum,
  PermissionCodeEnum,
  PermissionTypeEnum,
  ProductPermissionEnum,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import Icon from 'src/ui/atoms/icon';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import Header from 'ui/atoms/header';
import Button from 'src/ui/atoms/button';
import * as Styled from './styled';
import FormGroup from 'src/ui/molecules/form-group';
import SelectAtom from 'src/ui/atoms/select';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { debounce } from 'lodash';
import { currentUserHasManageEnvironmentPermission } from 'src/apps/issuer/helpers/userPermissions';
import { currentOrganizationHasManageOrganizationPermission } from 'src/apps/issuer/helpers/organizationPermissions';

interface AddPermissionModalProps {
  group: AdminGroup;
  onAddGroupPermission: () => void;
  onClose: () => void;
  groupOrganization: AdminOrganizationDetails | null;
}

interface AddPermissionFields {
  permissionType: PermissionTypeEnum;
  organization?: string;
  issuer?: string;
  product?: string;
  distributionPlatform?: string;
  permissionCode: EnvironmentPermissionsEnum;
}

export const Form = makeModalForm<AddPermissionFields>();

const AddPermissionModal: FunctionComponent<AddPermissionModalProps> = ({
  group,
  onAddGroupPermission,
  onClose,
  groupOrganization,
}) => {
  const [permissions, setPermissions] = useState<Array<string>>(['']);
  const [existingPermissions, setExistingPermissions] = useState<AdminGroupPermission[]>([]);
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [issuers, setIssuers] = useState<DropdownElement[]>([]);
  const [products, setProducts] = useState<DropdownElement[]>([]);
  const [distributionPlatforms, setDistributionPlatforms] = useState<DistributionPlatform[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<AdminOrganization | null>(null);
  const [selectedIssuer, setSelectedIssuer] = useState<DropdownElement | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<DropdownElement | null>(null);
  const [selectedDistributionPlatform, setSelectedDistributionPlatform] = useState<DistributionPlatform | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [selectedPermissionType, setSelectedPermissionType] = useState<PermissionTypeEnum | null>(null);

  const { makeAuthenticatedApi, withApi, error } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const { currentUser } = useCurrentUserSelector();
  const isPhone = useIsMedia(DEVICES.phone);
  const translate = useTranslateWithStringArgs();

  const fetchOrganizations = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await adminApi.adminOrganizationsList({
          search: searchQuery,
          limit: 50,
          ...(!currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) && {
            permissions: [AdminOrganizationsListPermissionsEnum.MANAGE_ORGANIZATION_PERMISSIONS],
          }),
        });
        setOrganizations(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const fetchIssuers = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await adminApi.adminIssuersDropdownList({
          search: searchQuery,
          limit: 50,
          ...(!currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) && {
            permissions: AdminIssuersDropdownListPermissionsEnum.MANAGE_ORGANIZATION_PERMISSIONS,
          }),
        });
        setIssuers(response.results || []);
      });
    },
    [withApi, adminApi, organizations.length, currentUser?.permissions],
  );

  const fetchProducts = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        const response = await adminApi.adminProductsDropdownList({
          search: searchQuery,
          limit: 50,
          ...(!currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) && {
            permissionOnOrganization:
              AdminProductsDropdownListPermissionOnOrganizationEnum.MANAGE_ORGANIZATION_PERMISSIONS,
          }),
        });
        setProducts(response.results || []);
      });
    },
    [withApi, adminApi, currentUser],
  );

  const fetchDistributionPlatforms = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        try {
          const response = await adminApi.adminDistributionPlatformsList({
            search: searchQuery,
            limit: 50,
            ...(!currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) && {
              permissions: [AdminDistributionPlatformsListPermissionsEnum.MANAGE_ORGANIZATION_PERMISSIONS],
            }),
          });
          setDistributionPlatforms(response.results || []);
        } catch (e) {
          console.error(e);
        }
      });
    },
    [withApi, adminApi, organizations.length, currentUser?.permissions],
  );

  useEffect(() => {
    switch (selectedPermissionType) {
      case PermissionTypeEnum.ISSUER:
        fetchIssuers();
        break;
      case PermissionTypeEnum.PRODUCT:
        fetchProducts();
        break;
      case PermissionTypeEnum.DISTRIBUTION_PLATFORM:
        fetchDistributionPlatforms();
        break;
    }
  }, [selectedPermissionType]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const searchOrganizationValue = useCallback(
    (value: string, action?: string) => {
      if (action === 'menu-close' && value === '') {
        return;
      }
      fetchOrganizations(value);
    },
    [fetchOrganizations],
  );

  const searchIssuerValue = useCallback(
    (value: string, action?: string) => {
      if (action === 'menu-close' && value === '') {
        return;
      }
      fetchIssuers(value);
    },
    [fetchIssuers],
  );

  const searchProductValue = useCallback(
    (value: string, action?: string) => {
      if (action === 'menu-close' && value === '') {
        return;
      }
      fetchProducts(value);
    },
    [fetchProducts],
  );

  const searchDistributionPlatformValue = useCallback(
    (value: string, action?: string) => {
      if (action === 'menu-close' && value === '') {
        return;
      }
      fetchDistributionPlatforms(value);
    },
    [fetchDistributionPlatforms],
  );

  const organizationsFilter = useMemo(
    () =>
      organizations.map((organization) => ({
        value: organization.id,
        label: organization.name,
        name: organization.name,
      })),
    [organizations],
  );

  const issuersFilter = useMemo(
    () =>
      issuers.map((issuer) => ({
        value: issuer.id,
        label: issuer.name,
        name: issuer.name,
      })),
    [issuers],
  );

  const productsFilter = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.name,
        name: product.name,
      })),
    [products],
  );

  const distributionPlatformsFilter = useMemo(
    () =>
      distributionPlatforms.map((distributionPlatform) => ({
        value: distributionPlatform.id,
        label: distributionPlatform.displayName,
        name: distributionPlatform.displayName,
      })),
    [distributionPlatforms],
  );

  const debouncedOrganizationSearch = useMemo(() => debounce(searchOrganizationValue, 500), [searchOrganizationValue]);
  const debouncedIssuerSearch = useMemo(() => debounce(searchIssuerValue, 500), [searchIssuerValue]);
  const debouncedProductSearch = useMemo(() => debounce(searchProductValue, 500), [searchProductValue]);
  const debouncedDistributionPlatformSearch = useMemo(
    () => debounce(searchDistributionPlatformValue, 500),
    [searchDistributionPlatformValue],
  );

  useEffect(() => {
    withApi(async () => {
      const response = await adminApi.adminGroupPermissionsList({ groupId: [group.id] });

      if (response && response.results) {
        setExistingPermissions(response.results);
      }
    });
  }, [group.id, adminApi, withApi]);

  useEffect(() => {
    setSubmitted(false);
  }, [permissions]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddPermissionFields>) => {
      withApi(async () => {
        setSubmitted(true);
        const hasEmptyPermissions = permissions.some((str) => str === '');
        if (hasEmptyPermissions) return;

        const apiRequests = permissions.map((code) => {
          const params: AdminGroupPermissionsCreateRequest = {
            adminGroupPermissionRequest: {
              group: group.id,
              permissionType: fieldsValues.permissionType,
              permissionCode: code as PermissionCodeEnum,
            },
          };

          if (fieldsValues.permissionType === PermissionTypeEnum.ORGANIZATION && selectedOrganization) {
            params.adminGroupPermissionRequest.objectId = selectedOrganization.id;
          }

          if (fieldsValues.permissionType === PermissionTypeEnum.ISSUER && selectedIssuer) {
            params.adminGroupPermissionRequest.objectId = selectedIssuer.id;
          }
          if (fieldsValues.permissionType === PermissionTypeEnum.PRODUCT && selectedProduct) {
            params.adminGroupPermissionRequest.objectId = selectedProduct.id;
          }
          if (
            fieldsValues.permissionType === PermissionTypeEnum.DISTRIBUTION_PLATFORM &&
            selectedDistributionPlatform
          ) {
            params.adminGroupPermissionRequest.objectId = selectedDistributionPlatform.id;
          }

          return adminApi.adminGroupPermissionsCreate(params);
        });
        await Promise.all(apiRequests);
        onAddGroupPermission();
      });
    },
    [
      [
        JSON.stringify(permissions),
        selectedOrganization?.id,
        selectedIssuer?.id,
        selectedProduct?.id,
        selectedDistributionPlatform?.id,
      ],
    ],
  );

  const getPermissionTypeFilterOptions = useMemo(() => {
    const options = [];

    if (currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) {
      options.push({
        value: PermissionTypeEnum.ENVIRONMENT,
        name: PermissionTypeEnum.ENVIRONMENT,
        label: translate(`permissionTypes.${snakeToCamelCaseWithNumbers(PermissionTypeEnum.ENVIRONMENT)}`),
      });
    }

    if (
      currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) ||
      (groupOrganization && currentOrganizationHasManageOrganizationPermission(groupOrganization))
    ) {
      options.push(
        {
          value: PermissionTypeEnum.ORGANIZATION,
          name: PermissionTypeEnum.ORGANIZATION,
          label: translate(`permissionTypes.${snakeToCamelCaseWithNumbers(PermissionTypeEnum.ORGANIZATION)}`),
        },
        {
          value: PermissionTypeEnum.ISSUER,
          name: PermissionTypeEnum.ISSUER,
          label: translate(`permissionTypes.${snakeToCamelCaseWithNumbers(PermissionTypeEnum.ISSUER)}`),
        },
        {
          value: PermissionTypeEnum.PRODUCT,
          name: PermissionTypeEnum.PRODUCT,
          label: translate(`permissionTypes.${snakeToCamelCaseWithNumbers(PermissionTypeEnum.PRODUCT)}`),
        },
        {
          value: PermissionTypeEnum.DISTRIBUTION_PLATFORM,
          name: PermissionTypeEnum.DISTRIBUTION_PLATFORM,
          label: translate(`permissionTypes.${snakeToCamelCaseWithNumbers(PermissionTypeEnum.DISTRIBUTION_PLATFORM)}`),
        },
      );
    }

    return options;
  }, [currentUser?.permissions, translate]);

  const environmentPermissionsOptions = useMemo(() => {
    return Object.values(EnvironmentPermissionsEnum).map((permissionCode) => ({
      value: permissionCode,
      label: translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
    }));
  }, [translate]);

  const organizationPermissionsOptions = useMemo(() => {
    return Object.values(OrganizationPermissionsEnum).map((permissionCode) => ({
      value: permissionCode,
      label: translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
    }));
  }, [translate]);

  const issuerPermissionsOptions = useMemo(() => {
    return Object.values(AdminIssuerDetailPermissionsEnum).map((permissionCode) => ({
      value: permissionCode,
      label: translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
    }));
  }, [translate]);

  const productPermissionsOptions = useMemo(() => {
    return Object.values(ProductPermissionEnum).map((permissionCode) => ({
      value: permissionCode,
      label: translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
    }));
  }, [translate]);

  const distributionPlatformPermissionsOptions = useMemo(() => {
    return Object.values(DistributionPlatformPermissionEnum).map((permissionCode) => ({
      value: permissionCode,
      label: translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
    }));
  }, [translate]);

  const getAvailablePermissionOptions = useCallback(
    (permission: string, permissionType: PermissionTypeEnum) => {
      const permissionsMap: Record<PermissionTypeEnum, Array<{ value: string; label: string }>> = {
        [PermissionTypeEnum.ENVIRONMENT]: environmentPermissionsOptions,
        [PermissionTypeEnum.ORGANIZATION]: organizationPermissionsOptions,
        [PermissionTypeEnum.PRODUCT]: productPermissionsOptions,
        [PermissionTypeEnum.ISSUER]: issuerPermissionsOptions,
        [PermissionTypeEnum.DISTRIBUTION_PLATFORM]: distributionPlatformPermissionsOptions,
      };

      let options: Array<{ value: string; label: string }> = permissionsMap[permissionType] || [];

      // filter out organization permissions of the selected organization based on service contract rules and already selected or existing permissions
      if (permissionType === PermissionTypeEnum.ORGANIZATION) {
        const hasRequiredContract = selectedOrganization?.hasScCore || selectedOrganization?.hasScLaunchpad;

        if (!hasRequiredContract) {
          options = options.filter(
            (option) =>
              option.value !== OrganizationPermissionsEnum.CREATE_ISSUERS_VIA_ISSUER_API &&
              option.value !== OrganizationPermissionsEnum.CREATE_ISSUERS_VIA_CASHLINK_STUDIO,
          );
        }
      }

      const entityIdMap: Partial<Record<PermissionTypeEnum, string | undefined>> = {
        [PermissionTypeEnum.ORGANIZATION]: selectedOrganization?.id,
        [PermissionTypeEnum.ISSUER]: selectedIssuer?.id,
        [PermissionTypeEnum.DISTRIBUTION_PLATFORM]: selectedDistributionPlatform?.id,
        [PermissionTypeEnum.PRODUCT]: selectedProduct?.id,
      };

      const targetId = entityIdMap[permissionType];

      // filter out environment permissions based on already selected or existing permissions
      return options.filter((option) => {
        if (option.value === permission) return true;

        const isAlreadySelected = permissions.includes(option.value);
        const existsOnBackend = existingPermissions.some((existPerm) => {
          if (permissionType === PermissionTypeEnum.ENVIRONMENT) {
            return existPerm.permissionCode === option.value;
          }
          return (
            existPerm.permissionType === permissionType &&
            existPerm.objectId === targetId &&
            existPerm.permissionCode === option.value
          );
        });

        return !isAlreadySelected && !existsOnBackend;
      });
    },
    [
      permissions,
      existingPermissions,
      environmentPermissionsOptions,
      organizationPermissionsOptions,
      productPermissionsOptions,
      issuerPermissionsOptions,
      distributionPlatformPermissionsOptions,
      selectedOrganization,
      selectedIssuer,
      selectedDistributionPlatform,
      selectedProduct,
    ],
  );

  const onUpdatePermission = useCallback(
    (index: number, value: string) => {
      const updated = [...permissions];
      updated[index] = value;
      setPermissions(updated);
    },
    [JSON.stringify(permissions)],
  );

  const onAddPermission = useCallback(() => {
    setPermissions([...permissions, '']);
  }, [JSON.stringify(permissions)]);

  const onRemovePermission = useCallback(
    (index: number) => {
      if (permissions.length > 1) {
        const updated = permissions.filter((_, i) => i !== index);
        setPermissions(updated);
      } else {
        setPermissions(['']);
      }
    },
    [JSON.stringify(permissions)],
  );

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addUserPermissionModal"
        onSubmit={(values: StateValues<AddPermissionFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name="addUserPermissionModal.title" />
        </ModalHeader>
        <ModalContent>
          <Form.ValueProvider>
            {({ permissionType, organization, issuer, product, distributionPlatform }) => (
              <>
                <Form.Group
                  name="permissionType"
                  required={true}
                  requiredError={<Translate name="addUserPermissionModal.fields.permissionType.requiredError" />}
                  tooltip={
                    <Tooltip
                      content={<Translate name="addUserPermissionModal.fields.permissionType.tooltip" />}
                      position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                      maxWidth={200}
                    >
                      <Icon name="question" circular={true} size="large" color="info" padded="none" />
                    </Tooltip>
                  }
                >
                  <Form.Select
                    onChange={(permission: PermissionTypeEnum) => setSelectedPermissionType(permission)}
                    options={getPermissionTypeFilterOptions}
                  ></Form.Select>
                </Form.Group>
                {permissionType === PermissionTypeEnum.ORGANIZATION && (
                  <Form.Group
                    name="organization"
                    required={permissionType === PermissionTypeEnum.ORGANIZATION}
                    occupyTooltipSpace
                    requiredError={<Translate name="addUserPermissionModal.fields.organization.requiredError" />}
                  >
                    <Form.Select
                      options={organizationsFilter}
                      onInputChange={debouncedOrganizationSearch}
                      onChange={(organizationId: string) => {
                        const foundOrganization = organizations.find((org) => org.id === organizationId) || null;
                        setSelectedOrganization(foundOrganization);
                      }}
                    ></Form.Select>
                  </Form.Group>
                )}
                {permissionType === PermissionTypeEnum.ISSUER && (
                  <Form.Group
                    name="issuer"
                    required={permissionType === PermissionTypeEnum.ISSUER}
                    occupyTooltipSpace
                    requiredError={<Translate name="addUserPermissionModal.fields.issuer.requiredError" />}
                  >
                    <Form.Select
                      options={issuersFilter}
                      onInputChange={debouncedIssuerSearch}
                      onChange={(issuerId: string) => {
                        const foundIssuer = issuers.find((issuer) => issuer.id === issuerId) || null;
                        setSelectedIssuer(foundIssuer);
                      }}
                    ></Form.Select>
                  </Form.Group>
                )}
                {permissionType === PermissionTypeEnum.PRODUCT && (
                  <Form.Group
                    name="product"
                    required={permissionType === PermissionTypeEnum.PRODUCT}
                    occupyTooltipSpace
                    requiredError={<Translate name="addUserPermissionModal.fields.product.requiredError" />}
                  >
                    <Form.Select
                      options={productsFilter}
                      onInputChange={debouncedProductSearch}
                      onChange={(productId: string) => {
                        const foundProduct = products.find((product) => product.id === productId) || null;
                        setSelectedProduct(foundProduct);
                      }}
                    ></Form.Select>
                  </Form.Group>
                )}
                {permissionType === PermissionTypeEnum.DISTRIBUTION_PLATFORM && (
                  <Form.Group
                    name="distributionPlatform"
                    required={permissionType === PermissionTypeEnum.DISTRIBUTION_PLATFORM}
                    occupyTooltipSpace
                    requiredError={
                      <Translate name="addUserPermissionModal.fields.distributionPlatform.requiredError" />
                    }
                  >
                    <Form.Select
                      options={distributionPlatformsFilter}
                      onInputChange={debouncedDistributionPlatformSearch}
                      onChange={(distributionPlatformId: string) => {
                        const foundDistributionPlatform =
                          distributionPlatforms.find(
                            (distributionPlatform) => distributionPlatform.id === distributionPlatformId,
                          ) || null;
                        setSelectedDistributionPlatform(foundDistributionPlatform);
                      }}
                    ></Form.Select>
                  </Form.Group>
                )}
                {(permissionType === PermissionTypeEnum.ENVIRONMENT ||
                  (permissionType === PermissionTypeEnum.ORGANIZATION && organization) ||
                  (permissionType === PermissionTypeEnum.ISSUER && issuer) ||
                  (permissionType === PermissionTypeEnum.PRODUCT && product) ||
                  (permissionType === PermissionTypeEnum.DISTRIBUTION_PLATFORM && distributionPlatform)) && (
                  <>
                    <Header spacing="small">
                      <Translate name="addUserPermissionModal.fields.permissionCode.heading" />
                    </Header>
                    {permissions.map((permission, index) => {
                      return (
                        <FormGroup
                          key={`permission-code-${index}`}
                          error={
                            permission.length === 0 && submitted ? (
                              <Translate name="addUserPermissionModal.fields.permissionCode.requiredError" />
                            ) : null
                          }
                          occupyTooltipSpace={permissions.length === 1}
                        >
                          <Styled.SelectContainer>
                            <SelectAtom
                              options={getAvailablePermissionOptions(permission, permissionType)}
                              value={permission}
                              onChange={(val) => onUpdatePermission(index, val)}
                              placeholder={translate('addUserPermissionModal.fields.permissionCode.label')}
                            ></SelectAtom>
                            {permissions.length > 1 && (
                              <Styled.DeleteIcon
                                name="trash"
                                color="primary"
                                size="large"
                                disabled={permissions.length === 1 && !permission}
                                onClick={() => onRemovePermission(index)}
                              />
                            )}
                          </Styled.SelectContainer>
                        </FormGroup>
                      );
                    })}
                    <Button variant="link" icon="plus" size="small" inline iconSize="small" onClick={onAddPermission}>
                      <Translate name="addUserPermissionModal.fields.permissionCode.add" />
                    </Button>
                  </>
                )}
              </>
            )}
          </Form.ValueProvider>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'add',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddPermissionModal;
